import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type { DecisionFooterProps } from './rich/decision-footer';

/**
 * A decision that can be answered together with the other pending decisions
 * of the same `key` (ENG-1945). A bulk request ("a £25 deposit on all five
 * services") raises one approval card per item; showing only the newest one
 * left the owner approving the same request five times, one card per turn.
 */
export interface ComposerGateBatch<T = unknown> {
  key: string;
  item: T;
  /** Builds the single decision for every pending item, oldest first. */
  combine: (items: T[]) => DecisionFooterProps;
}

export type ComposerGate = DecisionFooterProps & { batch?: ComposerGateBatch };

type Register = (id: string, gate: ComposerGate | null) => void;

/**
 * Split into TWO contexts on purpose, not one bundling `register` +
 * `activeGate` together. A single context whose value object changes
 * whenever `activeGate` changes would re-render every card calling
 * `useRegisterComposerGate` (they all subscribe to the same context to reach
 * `register`, stable or not) — and since each card builds a brand-new gate
 * object on every render, that re-render re-registers it, which recomputes
 * `activeGate` again, which re-renders every card again: an infinite loop
 * the moment two cards are mounted at once. Cards only ever need the STABLE
 * `register` function; only `Composer` needs the CHANGING `activeGate` value.
 */
const GateEnabledContext = createContext(true);
const GateTurnContext = createContext('');

/**
 * Historical turns must not reopen a decision after the owner has replied.
 * `turn` names the assistant turn this message belongs to (the owner message
 * that opened it), so batchable decisions only combine within one turn.
 */
export function ComposerGateScope({
  enabled,
  turn = '',
  children,
}: { enabled: boolean; turn?: string; children: React.ReactNode }) {
  return (
    <GateEnabledContext.Provider value={enabled}>
      <GateTurnContext.Provider value={turn}>
        {children}
      </GateTurnContext.Provider>
    </GateEnabledContext.Provider>
  );
}

/** The assistant turn a card belongs to; '' outside a scoped message list. */
export function useComposerTurnKey(): string {
  return useContext(GateTurnContext);
}

const RegisterContext = createContext<Register | null>(null);
const ActiveGateContext = createContext<DecisionFooterProps | null>(null);
const DecisionActionsDisabledContext = createContext(false);

export function useDecisionActionsDisabled() {
  return useContext(DecisionActionsDisabledContext);
}

/**
 * Blocks the composer's free-text input while a decision-shaped card
 * (confirm/reject a video draft, launch an ad, etc.) is unresolved.
 *
 * WHY A CONTEXT rather than a prop threaded through `MessageList` →
 * `ToolRenderer` → the card, mirroring `artifact-panel-context.tsx`'s own
 * documented reasoning for the identical shape of problem: the thing that
 * knows a card is still pending is buried several layers down in the message
 * list, and the thing that has to react (swap the composer's input area) is a
 * sibling of that list, not an ancestor. A card's resolved/unresolved state
 * is local React state — never present in the persisted message/envelope
 * data — so it has to be reported upward as it changes, not inferred.
 *
 * Only the MOST RECENTLY REGISTERED gate blocks the composer. This mirrors
 * the existing `superseded`/attempt-scoping rule that already governs which
 * staged-edit card is "live" when several appear in one transcript — only the
 * newest pending decision should ever be actionable.
 */
export function ComposerGateProvider({
  children,
  actionsDisabled = false,
}: {
  children: React.ReactNode;
  /** A detached turn may still own the saved approval shown after reload. */
  actionsDisabled?: boolean;
}) {
  return (
    <DecisionActionsDisabledContext.Provider value={actionsDisabled}>
      <ComposerGateProviderInner>{children}</ComposerGateProviderInner>
    </DecisionActionsDisabledContext.Provider>
  );
}

function ComposerGateProviderInner({
  children,
}: {
  children: React.ReactNode;
}) {
  const gatesRef = useRef(new Map<string, ComposerGate>());
  const orderRef = useRef<string[]>([]);
  const [activeGate, setActiveGate] = useState<DecisionFooterProps | null>(
    null
  );

  const recomputeActive = useCallback(() => {
    for (let i = orderRef.current.length - 1; i >= 0; i--) {
      const id = orderRef.current[i];
      const gate = gatesRef.current.get(id);
      if (gate) {
        // The newest pending decision is batchable: answer it together with
        // every pending sibling of the same kind, instead of hiding them.
        const batch = gate.batch;
        if (batch) {
          const items = orderRef.current.flatMap((siblingId) => {
            const sibling = gatesRef.current.get(siblingId)?.batch;
            return sibling?.key === batch.key ? [sibling.item] : [];
          });
          if (items.length > 1) {
            setActiveGate(batch.combine(items));
            return;
          }
        }
        setActiveGate(gate);
        return;
      }
    }
    setActiveGate(null);
  }, []);

  const register = useCallback<Register>(
    (id, gate) => {
      if (gate) {
        if (!gatesRef.current.has(id)) orderRef.current.push(id);
        gatesRef.current.set(id, gate);
      } else {
        gatesRef.current.delete(id);
        orderRef.current = orderRef.current.filter((x) => x !== id);
      }
      recomputeActive();
    },
    [recomputeActive]
  );

  return (
    <RegisterContext.Provider value={register}>
      <ActiveGateContext.Provider value={activeGate}>
        {children}
      </ActiveGateContext.Provider>
    </RegisterContext.Provider>
  );
}

/**
 * A card calls this with its own approval-footer props while it is pending a
 * decision, and `null` once resolved/rejected/fired/superseded/stale.
 *
 * Only subscribes to the STABLE register-function context — never
 * `activeGate` — so registering a card's own gate does not re-render every
 * OTHER card that also calls this hook.
 *
 * No-ops outside a provider (same contract as `useHasArtifactPanel`) — these
 * cards are also reused in surfaces with no composer (e.g. a batch-review
 * workspace), and a card that threw because of where it was mounted would be
 * a worse failure than one that quietly doesn't gate anything.
 */
export function useRegisterComposerGate(
  id: string,
  gate: ComposerGate | null
): boolean {
  const register = useContext(RegisterContext);
  const enabled = useContext(GateEnabledContext);

  // Forward controlled editor values before paint. A passive effect leaves
  // the composer rendering the previous value during the input event, which
  // lets React restore stale text before the next keystroke arrives.
  useLayoutEffect(() => {
    if (!register) return;
    register(id, enabled ? gate : null);
  }, [register, id, gate, enabled]);

  // Updating a gate must keep its original position in the pending queue.
  // Only remove it on unmount/id change, not on every selection or busy update.
  useEffect(() => () => register?.(id, null), [register, id]);
  return Boolean(register && enabled && gate);
}

/** Read by `Composer` to decide whether to swap the input area. */
export function useComposerGate(): { activeGate: DecisionFooterProps | null } {
  const activeGate = useContext(ActiveGateContext);
  return { activeGate };
}

/** Move an unresolved decision into the composer while keeping its owner mounted. */
export function ComposerDecision({
  id,
  gate,
  children,
}: {
  id: string;
  gate: ComposerGate | null;
  children: React.ReactNode;
}) {
  const enabled = useContext(GateEnabledContext);
  const moved = useRegisterComposerGate(
    id,
    gate ? { ...gate, content: children } : null
  );
  return moved || (!enabled && gate) ? null : children;
}

/** Whether this card belongs to the current unanswered assistant turn. */
export function useCurrentComposerTurn(): boolean {
  return useContext(GateEnabledContext);
}

/** Synchronous surface identity, including loading and completed cards. */
export function useHasComposerGate(): boolean {
  return useContext(RegisterContext) !== null;
}
